import { queryClient } from "@/appQueryClient";
import { config } from "@/config";
import { useAuthStore } from "@/modules/auth/AuthContext";
import { QuestionTypes } from "@/modules/beacons/schemas/core";

import { FormError, ResponseFormError } from "../../types/sharedTypes";

export const mountUrl = (path: string): string => {
  return `${config.api.baseUrl}${path}`;
};

export type FetcherError = {
  status: number;
  message: string;
  errors?: FormError[];
};

function addHeaders(options: RequestInit): RequestInit {
  const requestHeaders: HeadersInit = new Headers(options.headers);
  return {
    ...options,
    headers: requestHeaders,
    credentials: "include",
  };
}

/**
 * Simpler concurrent refresh handler using a shared refresh promise.
 * Ensures only one /auth/token request is in-flight and allows callers
 * to await the same promise. After a successful refresh we retry the
 * original request via fetcher. On failure we set authState to unauthenticated.
 */
let refreshPromise: Promise<Response> | null = null;

const refreshTokenOnce = async (): Promise<Response> => {
  if (!refreshPromise) {
    refreshPromise = fetch(
      mountUrl("/auth/token"),
      addHeaders({ method: "POST" })
    )
      .catch(() => {
        throw {
          status: 500,
          message: "Failed to refresh token: network error",
        };
      })
      .then((resp) => {
        if (!resp) throw { status: 500, message: "Empty refresh response" };
        if (resp.status === 200) {
          // Refresh succeeded - ask queryClient to refetch "me" so user data is up to date
          try {
            queryClient?.refetchQueries({ queryKey: ["me"] });
          } catch (err) {
            // swallow - queryClient may be null in some test setups
            console.warn(
              "Failed to refetch user data after token refresh",
              err
            );
          }
          return resp;
        }

        // Refresh failed - mark unauthenticated and surface error
        useAuthStore.setState({ authState: "unauthenticated" });
        throw { status: resp.status, message: "Credentials expired" };
      })
      .finally(() => {
        // clear after resolution so future refreshes can run when needed
        const current = refreshPromise;
        // small safety: only clear if it's the same promise
        if (current) refreshPromise = null;
      });
  }

  return refreshPromise;
};

const handleConcurrentRefreshToken = async (
  originalFetch: [string, RequestInit, Record<string, unknown>]
) => {
  await refreshTokenOnce();
  // retry original request after refresh
  return fetcher(...originalFetch);
};

export type FetcherDefaultError = {
  status: number;
  message: string;
};

export type FetcherValidationError = {
  status: number;
  message: string;
  errors: FormError[];
  meta?: Record<string, unknown>;
};

const removeQuestionTypeFromFieldname = (field: (string | number)[]) => {
  if (
    field[0] === "question" &&
    (Object.keys(QuestionTypes) as readonly string[]).includes(
      field?.[1].toString()
    )
  ) {
    field.splice(1, 1);
  }
  return field.map((f) => f.toString());
};

const handleResponse = async <T>(
  response: Response,
  originalFetch: [string, RequestInit, Record<string, unknown>]
) =>
  new Promise<T | null | FetcherError>((resolve, reject) => {
    if (response.ok) {
      return resolve(response.status === 204 ? null : response.json());
    }

    if (response.status === 422) {
      const rejectFormErrors = async (response: Response) => {
        const detail: ResponseFormError[] = (await response.json()).detail;
        const errors: FormError[] = Object.values(
          detail.reduce(
            (acc, { loc: [, ...field], msg, type }) => {
              let fieldName = field.map((f) => f.toString());
              if (fieldName.join(".") === "_general") {
                acc[`${fieldName}|${msg}`] = {
                  field: fieldName,
                  message: msg,
                  type,
                };
              } else {
                fieldName = removeQuestionTypeFromFieldname(field);

                const key = `${fieldName}|${type}`;
                acc[key] = acc[key]
                  ? { ...acc[key], message: `${acc[key].message}\n${msg}` }
                  : { field: fieldName, message: msg, type };
              }
              return acc;
            },
            {} as Record<string, FormError>
          )
        );
        const meta = originalFetch[2];

        return reject({
          status: 422,
          message: "Validation failed",
          errors,
          meta,
        } as FetcherValidationError);
      };

      return rejectFormErrors(response);
    }

    const getDefaultError = async (response: Response) => {
      const detail: string | unknown = (await response.json())?.detail;
      return reject({
        status: response.status,
        message: typeof detail === "string" ? detail : "Unknown error",
      } as FetcherDefaultError);
    };

    if (response.status === 401) {
      if (
        originalFetch[0].includes("/auth/login") ||
        originalFetch[0].includes("/auth/mfa")
      ) {
        return getDefaultError(response);
      }

      return handleConcurrentRefreshToken(originalFetch)
        .then((data) => resolve(data as T))
        .catch(reject);
    }

    return getDefaultError(response);
  });

function trimSlashes(url: string) {
  return url.replace(/^\/+|\/+(?=\?)|\/+$/g, "");
}

function fetcher<T>(
  url: string,
  options: RequestInit,
  meta: Record<string, unknown>
): Promise<T> {
  const optionsWithHeaders = addHeaders(options);
  url = trimSlashes(url);

  const promise = fetch(url, optionsWithHeaders)
    .catch(() =>
      Promise.reject({
        status: 500,
        message: "Failed to fetch: network error",
      })
    )
    .then(async (data) =>
      handleResponse(data, [url, optionsWithHeaders, meta])
    ) as Promise<T>;

  // Safety: prevent browser unhandledrejection event.
  // The promise stays rejected; callers (React Query, HttpClient users)
  // still receive the error through their own .catch() / onError handlers.
  promise.catch(() => {});

  return promise;
}

export default fetcher;
